import { StrictMode, Suspense, lazy, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./responsive.css";
import { captureTracking } from "./lib/tracking.js";
import { getMeta } from "./lib/pageMeta.js";
import App from "./App.jsx";

const loadPrivacy = () => import("./pages/Privacy.jsx");
const loadTerms = () => import("./pages/Terms.jsx");
const loadHipaa = () => import("./pages/Hipaa.jsx");
const loadTelehealthConsent = () => import("./pages/TelehealthConsent.jsx");
const loadAbout = () => import("./pages/About.jsx");
const loadContact = () => import("./pages/Contact.jsx");
const loadCaliforniaPrivacy = () => import("./pages/CaliforniaPrivacy.jsx");
const loadBillOfRights = () => import("./pages/BillOfRights.jsx");
const loadCancellation = () => import("./pages/Cancellation.jsx");
const loadAccessibility = () => import("./pages/Accessibility.jsx");
const loadNonDiscrimination = () => import("./pages/NonDiscrimination.jsx");
const loadNotFound = () => import("./pages/NotFound.jsx");
const loadShippingPolicy = () => import("./pages/ShippingPolicy.jsx");
const loadPrograms = () => import("./pages/Programs.jsx");
const loadLearn = () => import("./pages/Learn.jsx");
const loadGlp1PlateauWhatToDo = () => import("./pages/learn/Glp1PlateauWhatToDo.jsx");
const loadDoesGlp1CauseMuscleLoss = () => import("./pages/learn/DoesGlp1CauseMuscleLoss.jsx");
const loadGlp1SideEffectsWhatToExpect = () => import("./pages/learn/Glp1SideEffectsWhatToExpect.jsx");
const loadLongevity = () => import("./pages/Longevity.jsx");
const loadStart = () => import("./pages/Start.jsx");
const loadMicrodose = () => import("./pages/Microdose.jsx");
const loadVerifyLegitimateOnlineGlp1Provider = () => import("./pages/learn/VerifyLegitimateOnlineGlp1Provider.jsx");
const loadIsCompoundedSemaglutideSafeAndLegal = () => import("./pages/learn/IsCompoundedSemaglutideSafeAndLegal.jsx");
const loadWhatIsLegitscriptCertification = () => import("./pages/learn/WhatIsLegitscriptCertification.jsx");
const loadCompoundedVsBrandedGlp1Article = () => import("./pages/learn/CompoundedVsBrandedGlp1Article.jsx");
const loadWhatToLookForOnlineWeightLossProgram = () => import("./pages/learn/WhatToLookForOnlineWeightLossProgram.jsx");
const loadHowDoesOnlineWeightLossEvaluationWork = () => import("./pages/learn/HowDoesOnlineWeightLossEvaluationWork.jsx");
const loadGlp1PrivacyHealthData = () => import("./pages/learn/Glp1PrivacyHealthData.jsx");
const loadWhatDoesMedicalWeightLossProgramCost = () => import("./pages/learn/WhatDoesMedicalWeightLossProgramCost.jsx");
const loadCompoundedGlp1BigBrands2026 = () => import("./pages/learn/CompoundedGlp1BigBrands2026.jsx");
const loadCompoundedSemaglutideCostGuide = () => import("./pages/learn/CompoundedSemaglutideCostGuide.jsx");
const loadCompoundedGlp1LegitSafetyChecklist = () => import("./pages/learn/CompoundedGlp1LegitSafetyChecklist.jsx");
const loadCompoundedGlp1503aExplainer = () => import("./pages/learn/CompoundedGlp1503aExplainer.jsx");
const loadCompoundedGlp1TelehealthChanges = () => import("./pages/learn/CompoundedGlp1TelehealthChanges.jsx");

const Privacy = lazy(loadPrivacy);
const Terms = lazy(loadTerms);
const Hipaa = lazy(loadHipaa);
const TelehealthConsent = lazy(loadTelehealthConsent);
const About = lazy(loadAbout);
const Contact = lazy(loadContact);
const CaliforniaPrivacy = lazy(loadCaliforniaPrivacy);
const BillOfRights = lazy(loadBillOfRights);
const Cancellation = lazy(loadCancellation);
const Accessibility = lazy(loadAccessibility);
const NonDiscrimination = lazy(loadNonDiscrimination);
const NotFound = lazy(loadNotFound);
const ShippingPolicy = lazy(loadShippingPolicy);
const Programs = lazy(loadPrograms);
const Learn = lazy(loadLearn);
const Glp1PlateauWhatToDo = lazy(loadGlp1PlateauWhatToDo);
const DoesGlp1CauseMuscleLoss = lazy(loadDoesGlp1CauseMuscleLoss);
const Glp1SideEffectsWhatToExpect = lazy(loadGlp1SideEffectsWhatToExpect);
const Longevity = lazy(loadLongevity);
const Start = lazy(loadStart);
const Microdose = lazy(loadMicrodose);
const VerifyLegitimateOnlineGlp1Provider = lazy(loadVerifyLegitimateOnlineGlp1Provider);
const IsCompoundedSemaglutideSafeAndLegal = lazy(loadIsCompoundedSemaglutideSafeAndLegal);
const WhatIsLegitscriptCertification = lazy(loadWhatIsLegitscriptCertification);
const CompoundedVsBrandedGlp1Article = lazy(loadCompoundedVsBrandedGlp1Article);
const WhatToLookForOnlineWeightLossProgram = lazy(loadWhatToLookForOnlineWeightLossProgram);
const HowDoesOnlineWeightLossEvaluationWork = lazy(loadHowDoesOnlineWeightLossEvaluationWork);
const Glp1PrivacyHealthData = lazy(loadGlp1PrivacyHealthData);
const WhatDoesMedicalWeightLossProgramCost = lazy(loadWhatDoesMedicalWeightLossProgramCost);
const CompoundedGlp1BigBrands2026 = lazy(loadCompoundedGlp1BigBrands2026);
const CompoundedSemaglutideCostGuide = lazy(loadCompoundedSemaglutideCostGuide);
const CompoundedGlp1LegitSafetyChecklist = lazy(loadCompoundedGlp1LegitSafetyChecklist);
const CompoundedGlp1503aExplainer = lazy(loadCompoundedGlp1503aExplainer);
const CompoundedGlp1TelehealthChanges = lazy(loadCompoundedGlp1TelehealthChanges);

// Preserve the existing instant-paint /start and /microdose shells on direct
// visits: warm only the requested route chunk before React replaces #root.
// Client-side navigation still uses the shared Suspense boundary below.
const directRouteLoaders = {
  "/privacy": loadPrivacy,
  "/privacy-policy": loadPrivacy,
  "/terms": loadTerms,
  "/hipaa": loadHipaa,
  "/notice-of-privacy-practices": loadHipaa,
  "/telehealth-consent": loadTelehealthConsent,
  "/about": loadAbout,
  "/contact": loadContact,
  "/california-privacy": loadCaliforniaPrivacy,
  "/bill-of-rights": loadBillOfRights,
  "/cancellation": loadCancellation,
  "/refund-policy": loadCancellation,
  "/accessibility": loadAccessibility,
  "/non-discrimination": loadNonDiscrimination,
  "/shipping-policy": loadShippingPolicy,
  "/programs": loadPrograms,
  "/learn": loadLearn,
  "/learn/glp-1-plateau-what-to-do": loadGlp1PlateauWhatToDo,
  "/learn/does-glp-1-cause-muscle-loss": loadDoesGlp1CauseMuscleLoss,
  "/learn/glp-1-side-effects-what-to-expect": loadGlp1SideEffectsWhatToExpect,
  "/learn/how-to-verify-a-legitimate-online-glp1-provider": loadVerifyLegitimateOnlineGlp1Provider,
  "/learn/is-compounded-semaglutide-safe-and-legal": loadIsCompoundedSemaglutideSafeAndLegal,
  "/learn/what-is-legitscript-certification": loadWhatIsLegitscriptCertification,
  "/learn/compounded-vs-branded-glp1": loadCompoundedVsBrandedGlp1Article,
  "/learn/what-to-look-for-in-an-online-weight-loss-program": loadWhatToLookForOnlineWeightLossProgram,
  "/learn/how-does-an-online-weight-loss-evaluation-work": loadHowDoesOnlineWeightLossEvaluationWork,
  "/learn/glp1-and-your-privacy-health-data": loadGlp1PrivacyHealthData,
  "/learn/what-does-a-medical-weight-loss-program-cost": loadWhatDoesMedicalWeightLossProgramCost,
  "/learn/compounded-glp1-big-brands-2026": loadCompoundedGlp1BigBrands2026,
  "/learn/compounded-semaglutide-cost-guide": loadCompoundedSemaglutideCostGuide,
  "/learn/compounded-glp1-legit-safety-checklist": loadCompoundedGlp1LegitSafetyChecklist,
  "/learn/compounded-glp1-503a-explainer": loadCompoundedGlp1503aExplainer,
  "/learn/compounded-glp1-telehealth-changes": loadCompoundedGlp1TelehealthChanges,
  "/longevity": loadLongevity,
  "/start": loadStart,
  "/microdose": loadMicrodose,
};

// Keep <head> metadata (tab title + description + social tags) in sync with the
// current route on client-side navigation. Static crawlers read the prerendered
// HTML; this handles the live browser tab for real visitors.
function setMetaTag(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLinkTag(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = getMeta(pathname);
    document.title = meta.title;
    setMetaTag("name", "description", meta.description);
    setMetaTag("property", "og:title", meta.title);
    setMetaTag("property", "og:description", meta.description);
    setMetaTag("property", "og:url", meta.url);
    setMetaTag("name", "twitter:title", meta.title);
    setMetaTag("name", "twitter:description", meta.description);
    setLinkTag("canonical", meta.url);
  }, [pathname]);
  return null;
}

// Capture UTM / referrer / first-touch attribution into localStorage on first paint.
// Values are read by getTrackingFields() when the future lead/email-capture form is wired up.
captureTracking();

// Remove prerender-injected JSON-LD (crawler-only): once the SPA boots, the
// route components inject and manage the same schemas at runtime, so leaving
// the static copies in place would duplicate them.
document
  .querySelectorAll("script[data-prerender-schema]")
  .forEach((el) => el.remove());

function RouteLoading() {
  return (
    <div
      aria-label="Loading page"
      aria-busy="true"
      style={{ minHeight: "100vh", background: "#f0f4f4" }}
    />
  );
}

function Root() {
  return (
    <StrictMode>
      <BrowserRouter>
        <RouteMeta />
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            <Route path="/" element={<App />} />
            <Route path="/preview" element={<App />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/hipaa" element={<Hipaa />} />
            <Route path="/telehealth-consent" element={<TelehealthConsent />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/california-privacy" element={<CaliforniaPrivacy />} />
            <Route path="/bill-of-rights" element={<BillOfRights />} />
            <Route path="/cancellation" element={<Cancellation />} />
            <Route path="/refund-policy" element={<Cancellation />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/notice-of-privacy-practices" element={<Hipaa />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/non-discrimination" element={<NonDiscrimination />} />
            <Route path="/learn" element={<Learn />} />
            {/* Retired duplicate slugs — permanent redirects to canonical articles
                (static hosting equivalent of a 301; see src/lib/redirects.js). */}
            <Route path="/learn/is-compounded-semaglutide-legit" element={<Navigate to="/learn/is-compounded-semaglutide-safe-and-legal" replace />} />
            <Route path="/learn/how-to-choose-a-safe-glp-1-provider" element={<Navigate to="/learn/how-to-verify-a-legitimate-online-glp1-provider" replace />} />
            <Route path="/learn/how-to-verify-legitscript-certification" element={<Navigate to="/learn/what-is-legitscript-certification" replace />} />
            <Route path="/learn/compounded-vs-branded-glp-1" element={<Navigate to="/learn/compounded-vs-branded-glp1" replace />} />
            <Route path="/learn/glp-1-pricing-what-to-watch-for" element={<Navigate to="/learn/what-does-a-medical-weight-loss-program-cost" replace />} />
            <Route path="/learn/glp-1-plateau-what-to-do" element={<Glp1PlateauWhatToDo />} />
            <Route path="/learn/does-glp-1-cause-muscle-loss" element={<DoesGlp1CauseMuscleLoss />} />
            <Route path="/learn/glp-1-side-effects-what-to-expect" element={<Glp1SideEffectsWhatToExpect />} />
            <Route path="/learn/how-to-verify-a-legitimate-online-glp1-provider" element={<VerifyLegitimateOnlineGlp1Provider />} />
            <Route path="/learn/is-compounded-semaglutide-safe-and-legal" element={<IsCompoundedSemaglutideSafeAndLegal />} />
            <Route path="/learn/what-is-legitscript-certification" element={<WhatIsLegitscriptCertification />} />
            <Route path="/learn/compounded-vs-branded-glp1" element={<CompoundedVsBrandedGlp1Article />} />
            <Route path="/learn/what-to-look-for-in-an-online-weight-loss-program" element={<WhatToLookForOnlineWeightLossProgram />} />
            <Route path="/learn/how-does-an-online-weight-loss-evaluation-work" element={<HowDoesOnlineWeightLossEvaluationWork />} />
            <Route path="/learn/glp1-and-your-privacy-health-data" element={<Glp1PrivacyHealthData />} />
            <Route path="/learn/what-does-a-medical-weight-loss-program-cost" element={<WhatDoesMedicalWeightLossProgramCost />} />
            <Route path="/learn/compounded-glp1-big-brands-2026" element={<CompoundedGlp1BigBrands2026 />} />
            <Route path="/learn/compounded-semaglutide-cost-guide" element={<CompoundedSemaglutideCostGuide />} />
            <Route path="/learn/compounded-glp1-legit-safety-checklist" element={<CompoundedGlp1LegitSafetyChecklist />} />
            <Route path="/learn/compounded-glp1-503a-explainer" element={<CompoundedGlp1503aExplainer />} />
            <Route path="/learn/compounded-glp1-telehealth-changes" element={<CompoundedGlp1TelehealthChanges />} />
            <Route path="/longevity" element={<Longevity />} />
            <Route path="/start" element={<Start />} />
            <Route path="/microdose" element={<Microdose />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </StrictMode>
  );
}

const pathname =
  window.location.pathname !== "/" && window.location.pathname.endsWith("/")
    ? window.location.pathname.slice(0, -1)
    : window.location.pathname;
const preloadCurrentRoute =
  directRouteLoaders[pathname] ||
  (pathname === "/" || pathname === "/preview" ? null : loadNotFound);

Promise.resolve(preloadCurrentRoute?.()).finally(() => {
  createRoot(document.getElementById("root")).render(<Root />);
});
